import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  Input,
  Textarea,
  Text,
  VStack,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Menu,
  MenuItem,
  Spinner,
  Center,
  useDisclosure,
  Badge,
  Flex,
  MenuButton,
  IconButton,
  MenuList,
  Select,
  Skeleton,
} from "@chakra-ui/react";
import { Plus, ChevronLeft } from "lucide-react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useToastNotification } from "@/lib/utils/useToastNotification";
import { useAccessCheck } from "@/hooks/useAccess";
import AddProcedureModal from "@/components/modals/AddProcedureModal";
import RescheduleModal, {
  RescheduleData,
} from "@/components/modals/RescheduleModal";
import { getFlagUrl } from "@/lib/utils/countries";

import { appointmentService } from "@/lib/services/appointmentService";
import { Procedures } from "@/types/procedures";
import { procedureService } from "@/lib/services/procedureService";
import CancelAppointmentModal from "@/components/modals/CancelAppointmentModal";
import SaveChangesModal from "@/components/modals/SaveChangesModal";
import AddPrescriptionModal from "@/components/modals/AddPrescriptionModal";
import { Edit2, Trash2, MoreVertical } from "lucide-react";
import { Appointment, Prescription } from "@/types/appointment";
import { getStatusColor } from "@/util/getStatusColor";


const APPOINTMENT_STATUSES: Appointment["status"][] = [
  "Scheduled",
  "Checked in",
  "Checked out",
  "Confirmed",
  "Completed",
];

const ViewAppointmentDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { showError, showSuccess } = useToastNotification();
  const access = useAccessCheck();

  // Modals
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const {
    isOpen: isProcedureOpen,
    onOpen: onProcedureOpen,
    onClose: onProcedureClose,
  } = useDisclosure();
  const {
    isOpen: isRescheduleOpen,
    onOpen: onRescheduleOpen,
    onClose: onRescheduleClose,
  } = useDisclosure();
  const {
    isOpen: isCancelReasonOpen,
    onOpen: onCancelReasonOpen,
    onClose: onCancelReasonClose,
  } = useDisclosure();
  const {
    isOpen: isPrescriptionOpen,
    onOpen: onPrescriptionOpen,
    onClose: onPrescriptionClose,
  } = useDisclosure();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [procedures, setProcedures] = useState<Procedures[]>([]);
  const [actionType, setActionType] = useState<
    "cancel" | "reschedule" | "save" | "status" | null
  >(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [editingProcedure, setEditingProcedure] = useState<Procedures | null>(
    null
  );
  const [countryCode, setCountryCode] = useState("+63");
  const [parsedPhoneNumber, setParsedPhoneNumber] = useState("");

  // Appointment display fields
  const [patientName, setPatientName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [date, setDate] = useState<string>("");
  const [time, setTime] = useState<string>("");
  const [primaryDoctor, setPrimaryDoctor] = useState("");
  const [appointmentType, setAppointmentType] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState<Appointment["status"]>("Scheduled");
  const [originalStatus, setOriginalStatus] = useState<Appointment["status"]>("Scheduled");
  const [originalDate, setOriginalDate] = useState<string>("");
  const [originalTime, setOriginalTime] = useState<string>("");
  const [availableProcedures, setAvailableProcedures] =
    useState<Procedures[]>();
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [prescriptionCurrentPage, setPrescriptionCurrentPage] = useState(1);
  const [editingPrescription, setEditingPrescription] = useState<Prescription | null>(null);

  // Reschedule modal state
  const [rescheduleData, setRescheduleData] = useState<RescheduleData>({
    requestor: "",
    appointment_date: date || "",
    appointment_time: time || "",
    reason: "",
  });
  const [rescheduleErrors, setRescheduleErrors] = useState<
    Partial<RescheduleData>
  >({});

  // Cancel reason
  const [cancelReason, setCancelReason] = useState("");

  // Fetch appointment detail
  useEffect(() => {
    fetchAppointmentDetail();
  }, [id]);

  useEffect(() => {
    const fetchProcedures = async () => {
      const fetchedProcedures = await procedureService.getProcedures();
      setAvailableProcedures(fetchedProcedures || []);
    };
    fetchProcedures();
  }, []);

  const fetchAppointmentDetail = async () => {
    setIsLoading(true);
    try {
      const foundAppointment = await appointmentService.getAppointment(
        id || ""
      );
      if (!foundAppointment) {
        showError("Appointment not found");
        return;
      }

      // Set fields
      setPatientName(foundAppointment.patient?.user_fullname || "");
      const fullPhone = foundAppointment.patient?.user_phone_number || "";
      const phoneMatch = fullPhone.match(/^(\+\d+)\s+(.+)$/);
      if (phoneMatch) {
        setCountryCode(phoneMatch[1]);
        setParsedPhoneNumber(phoneMatch[2]);
      } else {
        setParsedPhoneNumber(fullPhone);
      }
      setPhoneNumber(fullPhone);
      setDate(foundAppointment.appointment_date || "");
      setTime(foundAppointment.appointment_time || "");
      setOriginalDate(foundAppointment.appointment_date || "");
      setOriginalTime(foundAppointment.appointment_time || "");
      setPrimaryDoctor(foundAppointment.doctor?.user_fullname || "");
      setAppointmentType(foundAppointment.appointment_type || "");
      setNotes(foundAppointment.notes || "");
      console.log("foundAppointment", foundAppointment);
      setStatus(foundAppointment.status || "Scheduled");
      setOriginalStatus(foundAppointment.status || "Scheduled");
      setProcedures(foundAppointment.procedures || []);
      setPrescriptions(foundAppointment.prescriptions || []);
    } catch (error: any) {
      showError("Failed to load appointment");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = (newStatus: Appointment["status"]) => {
    if (!access.can_edit) return;
    
    setStatus(newStatus);
  };

  // Reschedule handlers
  useEffect(() => {
    if (isRescheduleOpen) {
      setRescheduleData({
        requestor: patientName,
        appointment_date: date,
        appointment_time: time,
        reason: "",
      });
      setRescheduleErrors({});
    }
  }, [isRescheduleOpen]);

  const handleRescheduleAppointment = () => {
    setRescheduleErrors({});
    const errors: Partial<RescheduleData> = {};
    if (!rescheduleData.requestor.trim()) errors.requestor = "Required";
    if (!rescheduleData.appointment_date) errors.appointment_date = "Required";
    if (!rescheduleData.appointment_time) errors.appointment_time = "Required";
    if (!rescheduleData.reason.trim()) errors.reason = "Required";

    if (Object.keys(errors).length > 0) {
      setRescheduleErrors(errors);
      return;
    }

    setDate(rescheduleData.appointment_date);
    setTime(rescheduleData.appointment_time);
    showSuccess(
      "Rescheduled",
      "Appointment rescheduled. Click 'Save Changes' to save."
    );
    onRescheduleClose();
  };

  // Cancel appointment handlers
  const handleCancelClick = () => onCancelReasonOpen();
  
  const handleCancelWithReason = async () => {
    if (!cancelReason.trim()) {
      showError("Please provide a reason for cancellation");
      return;
    }

    setIsProcessing(true);
    try {
      await appointmentService.cancelAppointment(id!, cancelReason);
      showSuccess("Success", "Appointment cancelled successfully");
      setStatus("Cancelled");
      setOriginalStatus("Cancelled");
      onCancelReasonClose();
      setCancelReason("");
    } catch (error: any) {
      showError(error.message || "Failed to cancel appointment");
      setStatus(originalStatus);
    } finally {
      setIsProcessing(false);
    }
  };

  // Save procedures
  const handleSaveProcedure = (procedureData: {
    name: string;
    description: string;
    price: number;
    procedure_id?: number;
  }) => {
    if (editingProcedure) {
      const updatedProcedures = procedures.map((p) => {
        if (p.id === editingProcedure.id) {
          return {
            ...p,
            name: procedureData.name,
            description: procedureData.description,
            price: procedureData.price,
            procedure: p.procedure
              ? {
                  ...p.procedure,
                  name: procedureData.name,
                  description: procedureData.description,
                  price: procedureData.price,
                }
              : undefined,
            procedure_id: procedureData.procedure_id || p.procedure_id,
          };
        }
        return p;
      });
      setProcedures(updatedProcedures);
      showSuccess("Success", "Procedure updated successfully");
    } else {
      const newProcedure: Procedures = {
        id:
          procedures.length > 0
            ? Math.max(...procedures.map((p) => p.id!)) + 1
            : 1,
        name: procedureData.name,
        description: procedureData.description,
        price: procedureData.price,
        procedure_id: procedureData.procedure_id,
        procedure: {
          id: procedureData.procedure_id || Date.now(),
          name: procedureData.name,
          description: procedureData.description,
          price: procedureData.price,
          status: "active",
        } as Procedures,
        status: "active",
      };
      setProcedures([...procedures, newProcedure]);
      showSuccess("Success", "Procedure added successfully");
    }
    setEditingProcedure(null);
    onProcedureClose();
  };

  const handleSavePrescription = (prescription: {
    type: string;
    description: string;
    notes: string;
  }) => {
    if (editingPrescription) {
      setPrescriptions(
        prescriptions.map((p) =>
          p.id === editingPrescription.id ? { ...p, ...prescription } : p
        )
      );
      showSuccess("Success", "Prescription updated successfully");
    } else {
      const existingIds = prescriptions
        .map((p) => p.id)
        .filter((id): id is number => typeof id === "number");

      const newPrescription: Prescription = {
        id: existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1,
        ...prescription,
      };
      setPrescriptions([...prescriptions, newPrescription]);
      showSuccess("Success", "Prescription added successfully");
    }
    setEditingPrescription(null);
    onPrescriptionClose();
  };

  const handleEditPrescription = (prescription: Prescription) => {
    setEditingPrescription(prescription);
    onPrescriptionOpen();
  };

  const handleRemovePrescription = (prescriptionId: number) => {
    setPrescriptions(prescriptions.filter((p) => p.id !== prescriptionId));
    showSuccess("Success", "Prescription removed successfully");
    const newTotalPages = Math.ceil((prescriptions.length - 1) / itemsPerPage);
    if (prescriptionCurrentPage > newTotalPages && newTotalPages > 0) {
      setPrescriptionCurrentPage(newTotalPages);
    }
  };

  const totalCost = useMemo(() => {
    return procedures.reduce((acc, proc) => {
      const price = proc?.procedure?.price || proc?.price || 0;
      const numericPrice =
        typeof price === "number" ? price : parseFloat(price as string) || 0;
      return acc + numericPrice;
    }, 0);
  }, [procedures]);

  // Save changes confirmation
  const handleSaveClick = () => {
    const hasChanges =
      procedures.length > 0 ||
      prescriptions.length > 0 ||
      date !== originalDate ||
      time !== originalTime ||
      status !== originalStatus;

    if (!hasChanges) return showError("No changes to save");

    setActionType("save");
    onConfirmOpen();
  };

  const handleConfirmAction = async () => {
    if (!actionType || !id) return;

    setIsProcessing(true);
    try {
      if (actionType === "save") {
        const updateData: any = {
          appointment_date: date,
          appointment_time: time,
          status: status,
        };
        if (procedures.length > 0) {
          updateData.procedures = procedures.map((p) => ({
            procedure_id: p.procedure?.id || p.id,
          }));
        }
        if (prescriptions.length > 0) {
          updateData.prescriptions = prescriptions;
          updateData.prescriptions_id = prescriptions.map((p) => p.id);
        }
        updateData.total_cost = Number(totalCost) || 0;
        await appointmentService.updateAppointment(id, updateData);
        showSuccess("Success", "Changes saved successfully");
        setOriginalDate(date);
        setOriginalTime(time);
        setOriginalStatus(status);
        setTimeout(() => navigate("/appointments"), 500);
      }
    } catch (error: any) {
      showError(error.message || "Failed to update appointment");
    } finally {
      setIsProcessing(false);
      setActionType(null);
      onConfirmClose();
    }
  };

  // Edit/remove procedure
  const handleEditProcedure = (procedure: Procedures) => {
    setEditingProcedure(procedure);
    onProcedureOpen();
  };

  const handleRemoveProcedure = (procedureId: number) => {
    setProcedures(procedures.filter((p) => p.id !== procedureId));
    showSuccess("Success", "Procedure removed successfully");
    const newTotalPages = Math.ceil((procedures.length - 1) / itemsPerPage);
    if (currentPage > newTotalPages && newTotalPages > 0)
      setCurrentPage(newTotalPages);
  };

  // Pagination
  const paginatedProcedures = procedures.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(procedures.length / itemsPerPage);
  const paginatedPrescriptions = prescriptions.slice(
    (prescriptionCurrentPage - 1) * itemsPerPage,
    prescriptionCurrentPage * itemsPerPage
  );
  const prescriptionTotalPages = Math.ceil(prescriptions.length / itemsPerPage);

  const LoadingSkeleton = () => (
    <VStack spacing={4} align="stretch">
      <Flex justify="space-between" align="center">
        <Skeleton height="40px" width="120px" />
        <Skeleton height="32px" width="180px" />
      </Flex>

      <Box>
        <Skeleton height="24px" width="200px" mb={4} />
        <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }} gap={4}>
          <Box>
            <Skeleton height="20px" width="100px" mb={2} />
            <Skeleton height="40px" width="100%" />
          </Box>
          <Box>
            <Skeleton height="20px" width="100px" mb={2} />
            <Skeleton height="40px" width="100%" />
          </Box>
        </Grid>
      </Box>

      <Box mt={6}>
        <Skeleton height="24px" width="200px" mb={4} />
        <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }} gap={4}>
          {[1, 2, 3, 4].map((i) => (
            <Box key={i}>
              <Skeleton height="20px" width="100px" mb={2} />
              <Skeleton height="40px" width="100%" />
            </Box>
          ))}
        </Grid>
        <Box mt={4}>
          <Skeleton height="20px" width="100px" mb={2} />
          <Skeleton height="80px" width="100%" />
        </Box>
      </Box>

      <Box mt={6}>
        <Flex justify="space-between" align="center" mb={4}>
          <Skeleton height="24px" width="150px" />
          <Skeleton height="36px" width="150px" />
        </Flex>
        <Skeleton height="200px" width="100%" />
      </Box>

      <Box mt={6}>
        <Flex justify="space-between" align="center" mb={4}>
          <Skeleton height="24px" width="150px" />
          <Skeleton height="36px" width="150px" />
        </Flex>
        <Skeleton height="200px" width="100%" />
      </Box>

      <Skeleton height="32px" width="200px" mt={4} />

      <HStack spacing={3} justify="flex-end" mt={6}>
        <Skeleton height="36px" width="150px" />
        <Skeleton height="36px" width="180px" />
        <Skeleton height="36px" width="120px" />
      </HStack>
    </VStack>
  );

  if (isLoading) {
    return (
      <AdminLayout>
        <Box
          minH="calc(100vh - 110px)"
          overflow="hidden"
          display="flex"
          flexDirection="column"
          bg="#FEFCF8"
          py={1}
          px={5}
        >
          <LoadingSkeleton />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box
        minH="calc(100vh - 110px)"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        bg="#FEFCF8"
        py={1}
        px={5}
      >
        <VStack spacing={4} align="stretch">
          {/* Header */}
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
            <HStack spacing={3}>
              <Box
                as="button"
                onClick={() => navigate(-1)}
                fontSize="sm"
                fontWeight="medium"
                display="flex"
                alignItems="center"
                gap={2}
                color="gray.600"
                _hover={{ color: "orange.500" }}
                transition="color 0.2s"
                cursor="pointer"
                bg="transparent"
                border="none"
                p={0}
                mb={{ base: 2, md: 4 }}
              >
                <ChevronLeft size={20} />
                Go Back
              </Box>
            </HStack>
            <HStack spacing={2}>
              <Text fontSize="sm" color={"black"} fontWeight="600">
                Appointment Status:
              </Text>
              {access.can_edit ? (
                <Select
                  value={status}
                  onChange={(e) => handleStatusChange(e.target.value as Appointment["status"])}
                  size="sm"
                  width="auto"
                  minW="150px"
                  borderColor={getStatusColor(status) + ".500"}
                  color={getStatusColor(status) + ".700"}
                  fontWeight="500"
                  _hover={{ borderColor: getStatusColor(status) + ".600" }}
                  _focus={{
                    borderColor: getStatusColor(status) + ".600",
                    boxShadow: `0 0 0 1px ${getStatusColor(status)}.600`,
                  }}
                >
                  {APPOINTMENT_STATUSES.map((statusOption) => (
                    <option key={statusOption} value={statusOption}>
                      {statusOption}
                    </option>
                  ))}
                </Select>
              ) : (
                <Badge
                  colorScheme={getStatusColor(status)}
                  px={3}
                  py={1}
                  variant="outline"
                  borderRadius="md"
                  fontSize="xs"
                  fontWeight="500"
                >
                  {status}
                </Badge>
              )}
            </HStack>
          </Flex>

          {/* Main Content */}
          <VStack spacing={{ base: 4, md: 6 }} align="stretch">
            {/* Patient Information */}
            <Heading
              size={{ base: "sm", md: "md" }}
              mb={4}
              fontWeight="semibold"
            >
              Patient Information
            </Heading>

            <Grid
              templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
              gap={4}
            >
              <FormControl>
                <FormLabel
                  fontSize={{ base: "xs", md: "sm" }}
                  fontWeight="400"
                  color="#0A1B39"
                >
                  Patient Name
                </FormLabel>
                <Input
                  value={patientName}
                  isReadOnly
                  size={{ base: "sm", md: "md" }}
                  fontSize={{ base: "xs", md: "sm" }}
                  bg="white"
                  _hover={{ borderColor: "#DC3611" }}
                  _focus={{
                    borderColor: "#DC3611",
                    boxShadow: "0 0 0 1px #DC3611",
                  }}
                  color="#0A1B39"
                  borderRadius="md"
                  borderColor="gray.200"
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  fontSize={{ base: "xs", md: "sm" }}
                  fontWeight="400"
                  color="#0A1B39"
                >
                  Phone Number
                </FormLabel>
                <HStack spacing={0}>
                  <Box
                    width="60px"
                    height={{ base: "32px", md: "40px" }}
                    bg="white"
                    borderRadius="md"
                    borderRightRadius="0"
                    border="1px solid"
                    borderColor="gray.200"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    _hover={{ borderColor: "#DC3611" }}
                  >
                    <Box
                      as="img"
                      src={getFlagUrl(countryCode)}
                      alt="flag"
                      width="24px"
                      height="24px"
                      borderRadius="50%"
                      border="0"
                    />
                  </Box>
                  <Input
                    value={phoneNumber}
                    isReadOnly
                    size={{ base: "sm", md: "md" }}
                    fontSize={{ base: "xs", md: "sm" }}
                    bg="white"
                    _hover={{ borderColor: "#DC3611" }}
                    _focus={{
                      borderColor: "#DC3611",
                      boxShadow: "0 0 0 1px #DC3611",
                    }}
                    color="#0A1B39"
                    borderRadius="md"
                    borderLeftRadius="0"
                    borderLeft="0"
                    borderColor="gray.200"
                  />
                </HStack>
              </FormControl>
            </Grid>

            {/* Appointment Details */}
            <Heading
              size={{ base: "sm", md: "md" }}
              mb={4}
              fontWeight="semibold"
            >
              Appointment
            </Heading>

            <VStack spacing={4} align="stretch">
              <Grid
                templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                gap={4}
              >
                <FormControl>
                  <FormLabel
                    fontSize={{ base: "xs", md: "sm" }}
                    fontWeight="400"
                    color="#0A1B39"
                  >
                    Date
                  </FormLabel>
                  <Input
                    value={date}
                    isReadOnly
                    size={{ base: "sm", md: "md" }}
                    fontSize={{ base: "xs", md: "sm" }}
                    bg="white"
                    _hover={{ borderColor: "#DC3611" }}
                    _focus={{
                      borderColor: "#DC3611",
                      boxShadow: "0 0 0 1px #DC3611",
                    }}
                    color="#0A1B39"
                    borderRadius="md"
                    borderColor="gray.200"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel
                    fontSize={{ base: "xs", md: "sm" }}
                    fontWeight="400"
                    color="#0A1B39"
                  >
                    Time
                  </FormLabel>
                  <Input
                    value={time}
                    isReadOnly
                    size={{ base: "sm", md: "md" }}
                    fontSize={{ base: "xs", md: "sm" }}
                    bg="white"
                    _hover={{ borderColor: "#DC3611" }}
                    _focus={{
                      borderColor: "#DC3611",
                      boxShadow: "0 0 0 1px #DC3611",
                    }}
                    color="#0A1B39"
                    borderRadius="md"
                    borderColor="gray.200"
                  />
                </FormControl>
              </Grid>

              <Grid
                templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                gap={4}
              >
                <FormControl>
                  <FormLabel
                    fontSize={{ base: "xs", md: "sm" }}
                    fontWeight="400"
                    color="#0A1B39"
                  >
                    Primary Doctor
                  </FormLabel>
                  <Input
                    value={primaryDoctor}
                    isReadOnly
                    size={{ base: "sm", md: "md" }}
                    fontSize={{ base: "xs", md: "sm" }}
                    bg="white"
                    _hover={{ borderColor: "#DC3611" }}
                    _focus={{
                      borderColor: "#DC3611",
                      boxShadow: "0 0 0 1px #DC3611",
                    }}
                    color="#0A1B39"
                    borderRadius="md"
                    borderColor="gray.200"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel
                    fontSize={{ base: "xs", md: "sm" }}
                    fontWeight="400"
                    color="#0A1B39"
                  >
                    Appointment Type
                  </FormLabel>
                  <Input
                    value={appointmentType}
                    isReadOnly
                    size={{ base: "sm", md: "md" }}
                    fontSize={{ base: "xs", md: "sm" }}
                    bg="white"
                    _hover={{ borderColor: "#DC3611" }}
                    _focus={{
                      borderColor: "#DC3611",
                      boxShadow: "0 0 0 1px #DC3611",
                    }}
                    color="#0A1B39"
                    borderRadius="md"
                    borderColor="gray.200"
                  />
                </FormControl>
              </Grid>

              <FormControl>
                <FormLabel
                  fontSize={{ base: "xs", md: "sm" }}
                  fontWeight="400"
                  color="#0A1B39"
                >
                  Notes
                </FormLabel>
                <Textarea
                  value={notes}
                  isReadOnly
                  rows={3}
                  size={{ base: "sm", md: "md" }}
                  fontSize={{ base: "xs", md: "sm" }}
                  bg="white"
                  _hover={{ borderColor: "#DC3611" }}
                  _focus={{
                    borderColor: "#DC3611",
                    boxShadow: "0 0 0 1px #DC3611",
                  }}
                  color="#0A1B39"
                  borderRadius="md"
                  borderColor="gray.200"
                />
              </FormControl>
            </VStack>

            <Box borderBottom="1px solid #E7E8EB" pb={2} mb={4}></Box>

            {/* Procedures */}
            <Flex justify="space-between" align="center" mb={4}>
              <Heading size={{ base: "sm", md: "md" }} fontWeight="semibold">
                Procedure
              </Heading>
              {access.can_edit && (
                <Button
                  leftIcon={<Plus size={16} />}
                  bg="#DC3611"
                  _hover={{ bg: "#bf3c1fff" }}
                  color="white"
                  size="sm"
                  fontSize={{ base: "xs", md: "sm" }}
                  onClick={onProcedureOpen}
                >
                  Add Procedure
                </Button>
              )}
            </Flex>

            {/* Procedures Table */}
            <Box
              borderRadius="lg"
              border="1px"
              borderColor="gray.200"
              overflow="hidden"
            >
              <Box overflowX="auto">
                <Table variant="simple" size={{ base: "sm", md: "md" }}>
                  <Thead bg="white">
                    <Tr>
                      <Th
                        fontFamily="Inter"
                        fontWeight="600"
                        fontSize="14px"
                        lineHeight="21px"
                        textTransform="none"
                        color="#0A1B39"
                      >
                        Procedure name
                      </Th>
                      <Th
                        fontFamily="Inter"
                        fontWeight="600"
                        fontSize="14px"
                        lineHeight="21px"
                        textTransform="none"
                        color="#0A1B39"
                      >
                        Description
                      </Th>
                      <Th
                        fontFamily="Inter"
                        fontWeight="600"
                        fontSize="14px"
                        lineHeight="21px"
                        isNumeric
                        textTransform="none"
                        color="#0A1B39"
                      >
                        Price
                      </Th>
                      {access.can_edit && <Th w="50px"></Th>}
                    </Tr>
                  </Thead>
                  <Tbody bg="white">
                    {procedures.length === 0 ? (
                      <Tr>
                        <Td colSpan={access.can_edit ? 4 : 3} textAlign="center" py={8}>
                          <Text color="gray.500" fontSize="sm">
                            No procedures added yet.
                          </Text>
                        </Td>
                      </Tr>
                    ) : (
                      paginatedProcedures.map((procedure) => (
                        <Tr key={procedure.id}>
                          <Td
                            fontSize={{ base: "xs", md: "sm" }}
                            fontWeight="small"
                          >
                            {procedure.procedure?.name}
                          </Td>
                          <Td
                            fontSize={{ base: "xs", md: "sm" }}
                            color="#0A1B39"
                          >
                            {procedure.procedure?.description}
                          </Td>
                          <Td
                            fontSize={{ base: "xs", md: "sm" }}
                            isNumeric
                            fontWeight="small"
                          >
                            ₱{procedure.procedure?.price}
                          </Td>
                          {access.can_edit && (
                            <Td bg="white">
                              <Menu>
                                <MenuButton
                                  as={IconButton}
                                  icon={<MoreVertical size={18} />}
                                  variant="ghost"
                                  size="sm"
                                  aria-label="Actions"
                                />
                                <MenuList>
                                  <MenuItem
                                    color="red.600"
                                    onClick={() =>
                                      handleRemoveProcedure(procedure.id!)
                                    }
                                  >
                                    Remove
                                  </MenuItem>
                                </MenuList>
                              </Menu>
                            </Td>
                          )}
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>

              {/* Pagination */}
              {procedures.length > itemsPerPage && (
                <Box borderTop="1px" borderColor="gray.200" p={3} bg="white">
                  <Flex justify="center" align="center">
                    <HStack spacing={1}>
                      <Button
                        size="xs"
                        onClick={() =>
                          currentPage > 1 && setCurrentPage(currentPage - 1)
                        }
                        isDisabled={currentPage === 1}
                      >
                        &lt;
                      </Button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <Button
                            key={page}
                            size="xs"
                            bg={currentPage === page ? "#DC3611" : "white"}
                            color={currentPage === page ? "white" : "black"}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </Button>
                        )
                      )}
                      <Button
                        size="xs"
                        onClick={() =>
                          currentPage < totalPages &&
                          setCurrentPage(currentPage + 1)
                        }
                        isDisabled={currentPage === totalPages}
                      >
                        &gt;
                      </Button>
                    </HStack>
                  </Flex>
                </Box>
              )}
            </Box>
            <Box borderBottom="1px solid #E7E8EB" pb={2} mb={4}></Box>

            {/* Prescriptions */}
            <Flex justify="space-between" align="center" mb={4}>
              <Heading size={{ base: "sm", md: "md" }} fontWeight="semibold">
                Prescription
              </Heading>
              {access.can_edit && (
                <Button
                  leftIcon={<Plus size={16} />}
                  bg="#DC3611"
                  _hover={{ bg: "#bf3c1fff" }}
                  color="white"
                  size="sm"
                  fontSize={{ base: "xs", md: "sm" }}
                  onClick={onPrescriptionOpen}
                >
                  Add Prescription
                </Button>
              )}
            </Flex>

            {/* Prescriptions Table */}
            <Box
              borderRadius="lg"
              border="1px"
              borderColor="gray.200"
              overflow="hidden"
            >
              <Box overflowX="auto">
                <Table variant="simple" size={{ base: "sm", md: "md" }}>
                  <Thead bg="white" position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th
                        fontFamily="Inter"
                        fontWeight="600"
                        fontSize="14px"
                        lineHeight="21px"
                        textTransform="none"
                        color="#0A1B39"
                        borderBottom="1px solid"
                        borderColor="gray.200"
                      >
                        Type
                      </Th>
                      <Th
                        fontFamily="Inter"
                        fontWeight="600"
                        fontSize="14px"
                        lineHeight="21px"
                        textTransform="none"
                        color="#0A1B39"
                        borderBottom="1px solid"
                        borderColor="gray.200"
                      >
                        Description
                      </Th>
                      <Th
                        fontFamily="Inter"
                        fontWeight="600"
                        fontSize="14px"
                        lineHeight="21px"
                        textTransform="none"
                        color="#0A1B39"
                        borderBottom="1px solid"
                        borderColor="gray.200"
                      >
                        Notes
                      </Th>
                      {access.can_edit && (
                        <Th
                          w="80px"
                          textAlign="center"
                          fontFamily="Inter"
                          fontWeight="600"
                          fontSize="14px"
                          lineHeight="21px"
                          textTransform="none"
                          color="#0A1B39"
                          borderBottom="1px solid"
                          borderColor="gray.200"
                        >
                          Actions
                        </Th>
                      )}
                    </Tr>
                  </Thead>
                  <Tbody bg="white">
                    {prescriptions.length === 0 ? (
                      <Tr>
                        <Td colSpan={access.can_edit ? 4 : 3} textAlign="center" py={8}>
                          <Text color="gray.500" fontSize="sm">
                            No prescriptions added yet.
                          </Text>
                        </Td>
                      </Tr>
                    ) : (
                      paginatedPrescriptions.map((prescription) => (
                        <Tr key={prescription.id} _hover={{ bg: "gray.50" }}>
                          <Td fontSize={{ base: "xs", md: "sm" }} color="#0A1B39">
                            {prescription.type}
                          </Td>
                          <Td
                            fontSize={{ base: "xs", md: "sm" }}
                            color="#0A1B39"
                            maxW={{ base: "150px", md: "300px" }}
                          >
                            <Text noOfLines={2}>{prescription.description}</Text>
                          </Td>
                          <Td
                            fontSize={{ base: "xs", md: "sm" }}
                            color="#0A1B39"
                            maxW={{ base: "150px", md: "200px" }}
                          >
                            <Text noOfLines={2}>{prescription.notes || "-"}</Text>
                          </Td>
                          {access.can_edit && (
                            <Td>
                              <Flex justify="center">
                                <Menu>
                                  <MenuButton
                                    as={IconButton}
                                    icon={<MoreVertical size={18} />}
                                    variant="ghost"
                                    size="sm"
                                    aria-label="Actions"
                                    _hover={{ bg: "gray.100" }}
                                  />
                                  <MenuList minW="150px">
                                    <MenuItem
                                      icon={<Edit2 size={16} />}
                                      onClick={() => handleEditPrescription(prescription)}
                                    >
                                      Edit
                                    </MenuItem>
                                    <MenuItem
                                      icon={<Trash2 size={16} />}
                                      onClick={() => handleRemovePrescription(prescription.id)}
                                      color="red.500"
                                      _hover={{ bg: "red.50" }}
                                    >
                                      Remove
                                    </MenuItem>
                                  </MenuList>
                                </Menu>
                              </Flex>
                            </Td>
                          )}
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>

              {/* Pagination */}
              {prescriptions.length > itemsPerPage && (
                <Box borderTop="1px" borderColor="gray.200" p={3} bg="white">
                  <Flex justify="center" align="center">
                    <HStack spacing={1}>
                      <Button
                        size="xs"
                        onClick={() =>
                          prescriptionCurrentPage > 1 &&
                          setPrescriptionCurrentPage(prescriptionCurrentPage - 1)
                        }
                        isDisabled={prescriptionCurrentPage === 1}
                      >
                        &lt;
                      </Button>
                      {Array.from({ length: prescriptionTotalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <Button
                            key={page}
                            size="xs"
                            bg={prescriptionCurrentPage === page ? "#DC3611" : "white"}
                            color={prescriptionCurrentPage === page ? "white" : "black"}
                            onClick={() => setPrescriptionCurrentPage(page)}
                          >
                            {page}
                          </Button>
                        )
                      )}
                      <Button
                        size="xs"
                        onClick={() =>
                          prescriptionCurrentPage < prescriptionTotalPages &&
                          setPrescriptionCurrentPage(prescriptionCurrentPage + 1)
                        }
                        isDisabled={prescriptionCurrentPage === prescriptionTotalPages}
                      >
                        &gt;
                      </Button>
                    </HStack>
                  </Flex>
                </Box>
              )}
            </Box>

            <Box borderBottom="1px solid #E7E8EB" pb={2} mb={4}></Box>

            {/* Total Cost */}
            <Heading size={{ base: "md", md: "lg" }} fontWeight="bold">
              Total Cost: ₱
              {typeof totalCost === "number"
                ? Number(totalCost).toFixed(2)
                : "0.00"}
            </Heading>
            <Box borderBottom="1px solid #E7E8EB" pb={2} mb={4}></Box>

            {/* Action Buttons */}
            <HStack spacing={3} justify="flex-end" flexWrap="wrap" mt={-6}>
              {access.can_delete &&
                (status === "Scheduled" || status === "Checked in") && (
                  <Button
                    variant="outline"
                    colorScheme="red"
                    fontWeight="500"
                    onClick={handleCancelClick}
                    size={{ base: "sm", md: "sm" }}
                    fontSize="13px"
                  >
                    Cancel Appointment
                  </Button>
                )}

              {access.can_edit && status === "Scheduled" && (
                <Button
                  bg="#06AED4"
                  _hover={{ bg: "#0593b8ff" }}
                  color="white"
                  fontWeight="500"
                  onClick={onRescheduleOpen}
                  size={{ base: "sm", md: "sm" }}
                  fontSize="13px"
                >
                  Reschedule Appointment
                </Button>
              )}

              {access.can_edit && status !== "Cancelled" && (
                <Button
                  bg="#DC3611"
                  _hover={{ bg: "#bf3c1fff" }}
                  color="white"
                  fontWeight="500"
                  size={{ base: "sm", md: "sm" }}
                  fontSize="13px"
                  onClick={handleSaveClick}
                  isLoading={isProcessing}
                  loadingText="Saving..."
                >
                  Save Changes
                </Button>
              )}
            </HStack>
          </VStack>
        </VStack>
      </Box>

      <SaveChangesModal
        isOpen={isConfirmOpen}
        onClose={onConfirmClose}
        onConfirm={handleConfirmAction}
        isProcessing={isProcessing}
        date={date}
        originalDate={originalDate}
        time={time}
        originalTime={originalTime}
        procedures={procedures}
        totalCost={totalCost}
      />

      {/* Procedure Modal */}
      <AddProcedureModal
        isOpen={isProcedureOpen}
        onClose={() => {
          setEditingProcedure(null);
          onProcedureClose();
        }}
        onSave={handleSaveProcedure}
        availableProcedures={availableProcedures}
        editingProcedure={editingProcedure}
      />

      {/* Reschedule Modal */}
      <RescheduleModal
        isOpen={isRescheduleOpen}
        onClose={onRescheduleClose}
        rescheduleData={rescheduleData}
        setRescheduleData={setRescheduleData}
        rescheduleErrors={rescheduleErrors}
        handleRescheduleAppointment={handleRescheduleAppointment}
        isProcessing={isProcessing}
      />

      <CancelAppointmentModal
        isOpen={isCancelReasonOpen}
        onClose={() => {
          if (status === "Cancelled" && originalStatus !== "Cancelled") {
            setStatus(originalStatus);
          }
          setCancelReason("");
          onCancelReasonClose();
        }}
        cancelReason={cancelReason}
        setCancelReason={setCancelReason}
        onConfirm={handleCancelWithReason}
        isProcessing={isProcessing}
      />

      <AddPrescriptionModal
        isOpen={isPrescriptionOpen}
        onClose={() => {
          setEditingPrescription(null);
          onPrescriptionClose();
        }}
        onSave={handleSavePrescription}
        editingPrescription={editingPrescription}
      />
    </AdminLayout>
  );
};

export default ViewAppointmentDetail;